import React, { useEffect, useState } from "react";
import { Button, Input, Textarea, Spinner } from "@/components/ui";
import { endpoints, apiError } from "@/lib/api";
import { Copy, Mail, Send, X, Check } from "lucide-react";

export function TeaserModal({ dealId, investorId, investorName, onClose }) {
  const [draft, setDraft] = useState(null);
  const [err, setErr] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [to, setTo] = useState("");
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentMsg, setSentMsg] = useState("");

  useEffect(() => {
    endpoints.draftTeaser(dealId, investorId)
      .then((r) => { setDraft(r.data); setSubject(r.data.subject); setBody(r.data.body); setTo(r.data.suggested_to || ""); })
      .catch((e) => setErr(apiError(e)));
  }, [dealId, investorId]);

  const copy = async () => {
    await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  const mailto = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const send = async () => {
    setSending(true); setSentMsg("");
    try { await endpoints.sendOutreach({ draft_id: draft.id, to, subject, body }); setSentMsg(`Sent to ${to}`); }
    catch (e) { setSentMsg("Error: " + apiError(e)); } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md" onClick={onClose} data-testid="teaser-modal-overlay">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-lg p-6"
        style={{ background: "#0B1530", border: "1px solid rgba(197,160,89,0.35)" }} data-testid="teaser-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline text-[9px] text-gold mb-1">Anonymised investor teaser</div>
            <div className="font-display text-xl">{draft?.codename || "Drafting…"} → {investorName}</div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="teaser-close"><X size={18} /></button>
        </div>
        {err && <div className="text-red-300 text-sm" data-testid="teaser-error">{err}</div>}
        {!draft && !err && <div className="flex items-center gap-2 text-white/50 text-sm py-8"><Spinner size={14} /> Clara is drafting a blind teaser from the match reasons…</div>}
        {draft && (
          <>
            <label className="block text-[10px] uppercase tracking-[0.14em] text-white/40 mb-1">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="teaser-subject" className="mb-3" />
            <label className="block text-[10px] uppercase tracking-[0.14em] text-white/40 mb-1">Body (edit freely)</label>
            <Textarea rows={13} value={body} onChange={(e) => setBody(e.target.value)} data-testid="teaser-body" />
            <div className="text-[10.5px] text-white/35 mt-1 mb-4">The target is referred to only by codename. Review before sending.</div>
            <label className="block text-[10px] uppercase tracking-[0.14em] text-white/40 mb-1">Recipient</label>
            <Input type="email" placeholder="partner@investor.com" value={to} onChange={(e) => setTo(e.target.value)} data-testid="teaser-to" className="mb-4" />
            <div className="flex flex-wrap gap-2 justify-end">
              <Button variant="outline" onClick={copy} data-testid="teaser-copy">{copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}</Button>
              <a href={mailto} data-testid="teaser-mailto"
                className="inline-flex items-center gap-2 px-4 py-2 text-[13px] rounded-md border border-white/15 text-white/85 hover:border-[#C5A059] hover:text-gold transition-colors">
                <Mail size={13} /> Open in email
              </a>
              <Button onClick={send} disabled={sending || !/^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/.test(to)} data-testid="teaser-send">
                {sending ? <Spinner size={13} /> : <Send size={13} />} Send via Clara
              </Button>
            </div>
            {sentMsg && <div className={`text-[12px] mt-3 ${sentMsg.startsWith("Error") ? "text-red-300" : "text-emerald-300"}`} data-testid="teaser-send-msg">{sentMsg}</div>}
          </>
        )}
      </div>
    </div>
  );
}
